/* eslint-disable @typescript-eslint/no-explicit-any */
import { Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { EffectNotification, OnInitEffects, createEffect, OnRunEffects, Actions, ofType } from '@ngrx/effects';
import { withLatestFrom, catchError, exhaustMap, switchMap, takeUntil, first, map } from 'rxjs/operators';

import { NavigationErrorService } from '@shared/services';
import { fromCheckoutActions } from './checkout.actions';

import { checkoutQuery } from './checkout.selectors';

import { PaymentsService } from '@shared/services/payments/payments.service';
import { authenticationQuery } from '@shared/state/selectors/authentication/authentication.selectors';
import { AuthenticationActionTypes } from '@shared/state/actions/authentication/authentication.actions';
import { LoadingService } from '@shared/services/loading/loading.service';
@Injectable()
export class CheckoutEffects implements OnRunEffects, OnInitEffects {
  constructor(
    private _navigationErrorService: NavigationErrorService,
    private _paymentsService: PaymentsService,
    private _loadingService: LoadingService,
    private _actions$: Actions,
    private _store: Store
  ) {}

  getQuoteToSend$ = createEffect(() =>
    this._actions$.pipe(
      ofType(fromCheckoutActions.getQuoteToSend),
      switchMap(({ payload }) =>
        this._paymentsService.getPaymentQuote$(payload.id).pipe(
          map((result: any) => {
            this._loadingService.stop();
            return fromCheckoutActions.setQuoteToSend({ payload: result });
          }),
          catchError(e => {
            this._navigationErrorService.navigateToErrorHandlerPage(e?.status);

            this._loadingService.stop();

            return of(
              fromCheckoutActions.setQuoteToSendFailure({
                payload: { status: e.status }
              })
            );
          })
        )
      )
    )
  );

  getSelectedCreditBundle$ = createEffect(() =>
    this._actions$.pipe(
      ofType(fromCheckoutActions.getSelectedCreditBundle),
      switchMap(({ payload }) =>
        this._paymentsService.getCreditBundle$(payload.id).pipe(
          map((result: any) => {
            this._loadingService.stop();
            return fromCheckoutActions.setSelectedCreditBundle({
              payload: result
            });
          }),
          catchError(e => {
            // SEE IF IT EXIST IN STORE:
            return this._store.select(checkoutQuery.getSelectedCreditBundle).pipe(
              first(),
              switchMap(selectedBundle => {
                if (!selectedBundle || selectedBundle.uuid !== payload.id) {
                  this._navigationErrorService.navigateToErrorHandlerPage(e?.status);

                  this._loadingService.stop();

                  return of(
                    fromCheckoutActions.setSelectedCreditBundleFailure({
                      payload: { status: e.status }
                    })
                  );
                } else {
                  return of(
                    fromCheckoutActions.setSelectedCreditBundle({
                      payload: selectedBundle
                    })
                  );
                }
              })
            );
          })
        )
      )
    )
  );

  getCreditBundles$ = createEffect(() =>
    this._actions$.pipe(
      ofType(fromCheckoutActions.getCreditBundles),
      switchMap(() =>
        /* VISIBLE stands for regular bundles */
        this._paymentsService.listCreditBundles$({ visible: true }).pipe(
          map((result: any) =>
            fromCheckoutActions.setCreditBundles({
              payload: result
            })
          ),
          catchError(e => {
            this._navigationErrorService.navigateToErrorHandlerPage(e?.status);

            this._loadingService.stop();

            return of(
              fromCheckoutActions.setCreditBundlesFailure({
                payload: { status: e.status }
              })
            );
          })
        )
      )
    )
  );

  getCards$ = createEffect(() =>
    this._actions$.pipe(
      ofType(fromCheckoutActions.getCards),
      withLatestFrom(this._store.select(authenticationQuery.getOrganisation)),
      switchMap(([, organisation]) => {
        return this._paymentsService.listBuckets$(organisation.uuid);
      }),
      switchMap(buckets => {
        const bucket = buckets?.data[0];

        return this._paymentsService.listBucketCards$(bucket.uuid).pipe(
          map(result =>
            fromCheckoutActions.setCards({
              payload: result?.data
            })
          ),
          catchError(e => {
            return of(
              fromCheckoutActions.setCardsFailure({
                payload: { status: e.status }
              })
            );
          })
        );
      })
    )
  );

  getSpecialCreditBundle$ = createEffect(() =>
    this._actions$.pipe(
      ofType(fromCheckoutActions.getSpecialCreditBundle),
      switchMap(({ payload }) =>
        this._paymentsService.getCreditBundle$(payload.id).pipe(
          map((result: any) => fromCheckoutActions.setSpecialCreditBundle({ payload: result })),
          catchError(e => {
            this._navigationErrorService.navigateToErrorHandlerPage(e?.status);

            this._loadingService.stop();

            return of(
              fromCheckoutActions.setSpecialCreditBundleFailure({
                payload: { status: e.status }
              })
            );
          })
        )
      )
    )
  );

  ngrxOnInitEffects(): Action {
    return { type: '[CheckoutEffects]: Init' };
  }

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>) {
    return this._actions$.pipe(
      ofType(AuthenticationActionTypes.Login, '[CheckoutEffects]: Init'),
      exhaustMap(() => resolvedEffects$.pipe(takeUntil(this._actions$.pipe(ofType(AuthenticationActionTypes.Logout)))))
    );
  }
}
